import React, { useState, useRef, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  InputGroup,
  InputRightElement,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Box,
  Spinner,
  Switch,
  FormHelperText,
  HStack
} from "@chakra-ui/react";
import { FaGlobe } from "react-icons/fa";

const DataTab = ({ accentColor, settings, setSettings }) => {
  // Try to load website URL from localStorage or use a default
  const [websiteUrl, setWebsiteUrl] = useState(() => {
    const savedUrl = localStorage.getItem('websiteUrl');
    return savedUrl || "https://";
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [searchFullPage, setSearchFullPage] = useState(() => {
    const savedSetting = localStorage.getItem('searchFullPage');
    return savedSetting ? JSON.parse(savedSetting) : false;
  });
  const urlInputRef = useRef(null);
  
  // Auto-select pages after extraction and update settings
  const autoSelectPagesAndUpdateSettings = (pages) => {
    if (!pages || pages.length === 0) return [];
    
    // Find important pages
    const mainPageIndex = pages.findIndex(page => page.url === websiteUrl);
    const headerIndex = pages.findIndex(page => page.url.toLowerCase() === 'header');
    const footerIndex = pages.findIndex(page => page.url.toLowerCase() === 'footer');
    
    // Start with critical pages
    const selectedIndices = [];
    const characterLimit = 50000; // Same as in the Data page
    let currentCharCount = 0;
    
    // Helper function to add page if it exists
    const addPageIfExists = (index) => {
      if (index !== -1) {
        selectedIndices.push(index);
        currentCharCount += pages[index].length || 0;
        return true;
      }
      return false;
    };
    
    // Add critical pages first
    addPageIfExists(headerIndex);
    addPageIfExists(mainPageIndex);
    addPageIfExists(footerIndex);
    
    // Medium pages (1000-10000 chars)
    pages.forEach((page, index) => {
      const length = page.length || 0;
      if (length >= 1000 && length <= 10000 && 
          ![headerIndex, mainPageIndex, footerIndex].includes(index) &&
          currentCharCount + length <= characterLimit) {
        selectedIndices.push(index);
        currentCharCount += length;
      }
    });
    
    // Small pages (<1000 chars)
    pages.forEach((page, index) => {
      const length = page.length || 0;
      if (length < 1000 && 
          ![headerIndex, mainPageIndex, footerIndex].includes(index) &&
          !selectedIndices.includes(index) &&
          currentCharCount + length <= characterLimit) {
        selectedIndices.push(index);
        currentCharCount += length;
      }
    });
    
    // Large pages (>10000 chars)
    pages.forEach((page, index) => {
      const length = page.length || 0;
      if (length > 10000 && 
          ![headerIndex, mainPageIndex, footerIndex].includes(index) &&
          !selectedIndices.includes(index) &&
          currentCharCount + length <= characterLimit) {
        selectedIndices.push(index);
        currentCharCount += length;
      }
    });
    
    // Save selected indices to localStorage
    localStorage.setItem('checkedPages', JSON.stringify(selectedIndices));
    
    // Update settings with context text from selected pages
    const selectedPages = selectedIndices.map(i => pages[i]);
    const contextText = selectedPages.map(page => page.contextText).join('\n\n');
    
    // Update settings object with new context text
    const updatedSettings = { 
      ...settings, 
      contextText: contextText
    };
    
    // Update React state and localStorage
    setSettings(updatedSettings);
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    
    return selectedIndices;
  };

  const extractData = async () => {
    if (!websiteUrl.trim() || websiteUrl === "https://") {
      setExtractionResult({
        status: "error",
        message: "Please enter a valid website URL"
      });
      return;
    }

    setIsLoading(true);
    setExtractionResult(null);

    try {
      // Save the website URL to localStorage
      localStorage.setItem('websiteUrl', websiteUrl);
      // Save the search full page setting
      localStorage.setItem('searchFullPage', JSON.stringify(searchFullPage));
      
      // Call the same API used in the Data page
      const response = await fetch(
        `https://9eakvq982a.execute-api.eu-north-1.amazonaws.com/?url=${websiteUrl}&search_full_page=${searchFullPage}`
      );
      
      const data = await response.json();
      
      if (!data.pages || data.pages.length === 0) {
        setExtractionResult({
          status: "warning",
          message: "No data found. Try a different URL or search mode."
        });
        
        // Clear stored API data
        localStorage.removeItem('apiData');
      } else {
        // Save API data to localStorage for the Data page to access
        localStorage.setItem('apiData', JSON.stringify(data.pages));
        
        // Auto-select pages and update settings.json
        const selectedPages = autoSelectPagesAndUpdateSettings(data.pages);
        
        setExtractionResult({
          status: "success",
          message: `Successfully extracted ${data.pages.length} pages, selected ${selectedPages.length} for context!`,
          count: data.pages.length,
          selected: selectedPages.length
        });
      }
    } catch (error) {
      console.error('Error extracting data:', error);
      setExtractionResult({
        status: "error",
        message: "Failed to extract website data. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearResult = () => {
    setExtractionResult(null);
  };

  return (
    <VStack spacing={6} align="stretch">
      <Text fontSize="sm" mb={2}>
        Extract website content for your chatbot to use as context. 
        You can refine the date later in the Data page.
      </Text>
      
      <FormControl>
        <FormLabel>Website URL</FormLabel>
        <InputGroup>
          <Input
            ref={urlInputRef}
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://example.com"
            size="md"
            pr="4.5rem"
            isDisabled={isLoading}
          />
          <InputRightElement width="3rem">
            <FaGlobe color="gray" />
          </InputRightElement>
        </InputGroup>
      </FormControl>
      
      <FormControl display="flex" alignItems="center">
        <FormLabel htmlFor="search-full-page" mb="0" fontSize="sm">
          Deep scan (may be slower)
        </FormLabel>
        <Switch 
          id="search-full-page"
          colorScheme="green"
          isChecked={searchFullPage}
          onChange={() => setSearchFullPage(!searchFullPage)}
          isDisabled={isLoading}
        />
      </FormControl>
      
      <Button
        onClick={extractData}
        bg={accentColor}
        color="white"
        size="md"
        width="full"
        _hover={{ bg: `${accentColor}80` }}
        isLoading={isLoading}
        loadingText="Extracting..."
      >
        Extract Website Data
      </Button>
      
      {/* Result notification */}
      {extractionResult && (
        <Alert 
          status={extractionResult.status} 
          variant="left-accent" 
          mt={2} 
          borderRadius="md"
        >
          <AlertIcon />
          <Box flex="1">
            <AlertTitle mb={0}>
              {extractionResult.status === "success" ? "Success!" : 
               extractionResult.status === "warning" ? "No Data Found" : "Error"}
            </AlertTitle>
            <AlertDescription display="block">
              {extractionResult.message}
              {extractionResult.status === "success" && (
                <Text mt={1} fontWeight="bold">
                  Settings updated with extracted content. Check the Data page for detailed customization.
                </Text>
              )}
            </AlertDescription>
          </Box>
          <CloseButton 
            alignSelf="flex-start" 
            position="relative" 
            right={-1} 
            top={-1} 
            onClick={handleClearResult} 
          />
        </Alert>
      )}
      
      <Text fontSize="xs" color="gray.500" mt={2}>
        Note: This extracts basic website content and automatically selects the most relevant pages. 
        For more detailed context customization, please visit the Data page after extraction.
      </Text>
    </VStack>
  );
};

export default DataTab;